import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  IconButton,
  Progress,
  Text,
} from '@chakra-ui/react';
import { useGetBusinessOverviewOfferDetailsQuery } from '@client/graphql/__generated__/main-operations';
import { BizOverviewOfferFragment } from '@client/graphql/__generated__/types';
import { XIcon } from 'lucide-react';
import { useState } from 'react';
import { useIsMobile } from '~/common/hooks/useIsMobile';
import { DocumentsThumbnailsWidgetWithContext } from '~/services/document/components/DocumentMapperWidgets/DocumentsThumbnailsWidget/DocumentsThumbnailsWidget';
import { PDFScrollable } from '~/services/document/components/PDFScrollable/PDFScrollable';
import {
  DocumentsThumbnails,
  VisiblePage,
} from '~/services/document/components/PDFScrollable/types';
import { OfferDrawerSidebar } from './OfferDrawerSidebar';

interface ViewOfferDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  offer: BizOverviewOfferFragment;
}

export const ViewOfferDrawer = ({
  isOpen,
  onClose,
  offer,
}: ViewOfferDrawerProps) => {
  const [thumbnails, setThumbnails] = useState<DocumentsThumbnails>({});
  const [visiblePage, setVisiblePage] = useState<VisiblePage | null>(null);
  const isMobile = useIsMobile();

  const { data, loading } = useGetBusinessOverviewOfferDetailsQuery({
    variables: {
      offerId: offer.id,
    },
  });

  const pdfUrl = data?.getBusinessOverviewOfferDetails?.offerToPurchaseUrl;

  const offerDetails = data?.getBusinessOverviewOfferDetails?.metadata;

  return (
    <Drawer
      allowPinchZoom={true}
      autoFocus={false}
      blockScrollOnMount={false}
      isOpen={isOpen}
      size="full"
      onClose={onClose}
    >
      <DrawerOverlay
        backdropFilter="blur(4.5px)"
        background="rgba(12, 12, 42, 0.45)"
      />
      <DrawerContent
        backgroundColor="bg.mainDark"
        borderRadius="20px 20px 0px 0px"
        borderTop="1px solid"
        borderTopColor="card.border"
        width="80vw"
      >
        {loading && (
          <Progress
            isIndeterminate
            height="2px"
            position="absolute"
            top={0}
            width="100%"
          />
        )}
        <DrawerHeader>
          <Flex alignItems="center" justifyContent="space-between" width="100%">
            <Text fontSize="lg" fontWeight="medium">
              Offer To Purchase
            </Text>
            <IconButton
              aria-label="Close"
              color="whiteAlpha.500"
              icon={<XIcon />}
              variant="link"
              onClick={onClose}
            />
          </Flex>
        </DrawerHeader>
        <DrawerBody width="100%">
          {pdfUrl && (
            <Flex direction="column" gap={4}>
              <Box>
                {/* Inspiration from OfferFlow/PreviewTabContent */}
                <HStack alignItems="stretch" width="100%">
                  {!isMobile && (
                    <Box
                      maxHeight="calc(100vh - 100px)"
                      width={{ base: '120px', '2xl': '200px' }}
                    >
                      {visiblePage && (
                        <DocumentsThumbnailsWidgetWithContext
                          documentsThumbnails={thumbnails}
                          visiblePage={visiblePage}
                        />
                      )}
                    </Box>
                  )}
                  <Box
                    bgColor="gray.400"
                    height={isMobile ? '80vh' : 'calc(100vh - 100px)'}
                    overflow="hidden"
                    position="relative"
                    width="100%"
                  >
                    <PDFScrollable
                      defaultZoom={isMobile ? 1.5 : 1}
                      maxWidth={1200}
                      pdfFiles={[
                        {
                          url: pdfUrl,
                          name: 'offer-to-purchase.pdf',
                        },
                      ]}
                      onDocumentsThumbnailsChange={setThumbnails}
                      onVisiblePageChange={setVisiblePage}
                    />
                  </Box>
                  <OfferDrawerSidebar offerDetails={offerDetails} />
                </HStack>
              </Box>
            </Flex>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
